// Panel lateral («sheet») del sistema de diseño — para DETALLES que se abren
// desde una lista (ticket, registro) sin perder la lista de vista.
//
//   · Escritorio (≥ lg): panel a la derecha, alto completo, la lista sigue
//     visible a la izquierda (atenuada).
//   · Móvil / tableta: pantalla completa con «Regresar» (lista → detalle).
//
// Mismo contrato que <Modal> (open/onClose/title/footer/inShell) para poder
// cambiar uno por otro. Portal (no lo descuadra un ancestro con transform), Esc
// cierra, el foco entra al panel y vuelve a donde estaba al cerrar, scroll de
// fondo bloqueado. Tokens semánticos → claro/oscuro y paleta de la org.

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, X } from 'lucide-react';

export function Sheet({ open, onClose, title, children, footer, inShell = false, label = 'Detalle' }: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Portal al marco `.orion-shell` (hereda el tema de la org). */
  inShell?: boolean;
  /** Nombre accesible del panel si `title` no es texto. */
  label?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // onClose suele llegar inline (nueva función en cada render del padre): se lee
  // de un ref para que el efecto dependa SOLO de `open`. Si dependiera de onClose,
  // cada actualización del padre (p. ej. llega una respuesta) re-ejecutaría el
  // efecto y le robaría el foco al campo donde la persona está escribiendo.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[112] flex justify-end">
      <div className="absolute inset-0 hidden bg-black/40 backdrop-blur-[2px] lg:block" aria-hidden onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="os-sheet relative flex h-full w-full flex-col bg-card text-foreground shadow-2xl outline-none lg:w-[min(960px,calc(100vw-280px))] lg:rounded-l-2xl lg:border-l lg:border-border"
      >
        <header className="app-header-safe flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5 sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl px-2 text-sm font-medium text-foreground hover:bg-muted lg:hidden"
          >
            <ArrowLeft size={18} aria-hidden /> Regresar
          </button>
          <div className="min-w-0 flex-1 text-sm font-semibold">{title}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="hidden shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground lg:inline-flex"
          >
            <X size={18} />
          </button>
        </header>
        <div className="min-h-0 grow overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer != null && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>
        )}
      </div>
    </div>,
    (inShell && document.querySelector('.orion-shell')) || document.body,
  );
}
